<template>
  <div class="">
    <!-- Reorder Button -->
    <div class="mb-4 flex justify-end sticky top-0 z-30 py-2">
      <button
        @click="toggleTopicReorderMode"
        class="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 flex items-center space-x-1"
      >
        <template v-if="isTopicReorderMode">
          <CheckIcon class="h-4 w-4" />
          <span>Save Order</span>
        </template>
        <template v-else>
          <ArrowsUpDownIcon class="h-4 w-4" />
          <span>Reorder Topics</span>
        </template>
      </button>
    </div>

    <div class="h-[calc(100vh-260px)] overflow-y-auto relative">
      <div class="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <!-- Main Topics Table -->
          <div class="relative">
            <!-- Sticky Header -->
            <div class="sticky top-0 z-20">
              <div class="flex min-w-full">
                <div
                  class="w-[30%] py-3.5 pr-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100 sm:pl-0"
                >
                  Topic
                </div>
                <div
                  class="w-[15%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100"
                >
                  Part
                </div>
                <div
                  class="w-[15%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100"
                >
                  Category
                </div>
                <div
                  class="w-[15%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100"
                >
                  Is New
                </div>
                <div
                  class="w-[15%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100"
                >
                  Visibility
                </div>
                <div
                  class="w-[10%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100"
                >
                  <span class="sr-only">Edit</span>
                </div>
              </div>
            </div>

            <!-- Draggable Topics List -->
            <div class="divide-y divide-gray-200 dark:divide-gray-700">
              <draggable
                v-model="topics"
                :disabled="!isTopicReorderMode"
                item-key="topicId"
                @end="onTopicDragEnd"
                handle=".drag-handle"
              >
                <template #item="{ element: topic }">
                  <div>
                    <!-- Topic Row -->
                    <div
                      class="flex items-center min-w-full hover:bg-gray-50 dark:hover:bg-gray-700"
                      :class="{ 'bg-gray-50 dark:bg-gray-700': isTopicReorderMode }"
                    >
                      <div
                        class="py-3 pr-3 text-sm font-medium whitespace-nowrap text-gray-900 dark:text-gray-100 sm:pl-0 w-[30%]"
                      >
                        <div class="flex items-center space-x-2">
                          <button
                            v-if="!isTopicReorderMode"
                            class="text-gray-500 dark:text-gray-400"
                            @click="toggleQuestions(topic.topicId)"
                          >
                            <ChevronRightIcon
                              v-if="!expandedTopics[topic.topicId]"
                              class="h-5 w-5"
                            />
                            <ChevronDownIcon v-else class="h-5 w-5" />
                          </button>
                          <button
                            v-else
                            class="drag-handle cursor-move text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                          >
                            <Bars3Icon class="h-5 w-5" />
                          </button>
                          {{ topic.topicName }}
                        </div>
                      </div>
                      <div
                        class="px-3 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400 w-[15%]"
                      >
                        {{ topic.partId }}
                      </div>
                      <div
                        class="px-3 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400 w-[15%]"
                      >
                        <select
                          v-model="topic.categoryId"
                          class="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 dark:text-gray-300 rounded-md text-sm px-2 py-1"
                          @change="onCategoryChange(topic)"
                        >
                          <option
                            v-for="category in categories"
                            :key="category.id"
                            :value="category.id"
                          >
                            {{ category.name }}
                          </option>
                        </select>
                      </div>
                      <div
                        class="px-3 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400 w-[15%]"
                      >
                        <Switch
                          v-model="topic.isNew"
                          @click="onToggleIsNew(topic.topicId, !topic.isNew)"
                          :class="[
                            topic.isNew ? 'bg-indigo-400' : 'bg-gray-200 dark:bg-gray-600',
                            'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden',
                          ]"
                        >
                          <span
                            aria-hidden="true"
                            :class="[
                              topic.isNew ? 'translate-x-4' : 'translate-x-0',
                              'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white ring-0 shadow-sm transition duration-200 ease-in-out',
                            ]"
                          />
                        </Switch>
                      </div>
                      <div
                        class="px-3 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400 w-[15%]"
                      >
                        <Switch
                          v-model="topic.isVisible"
                          @click="onVisibilityChange(topic.topicId, !topic.isVisible)"
                          :class="[
                            topic.isVisible ? 'bg-indigo-400' : 'bg-gray-200 dark:bg-gray-600',
                            'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden',
                          ]"
                        >
                          <span class="sr-only">Use setting</span>
                          <span
                            aria-hidden="true"
                            :class="[
                              topic.isVisible ? 'translate-x-4' : 'translate-x-0',
                              'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white ring-0 shadow-sm transition duration-200 ease-in-out',
                            ]"
                          />
                        </Switch>
                      </div>
                      <div
                        class="relative py-4 pr-4 pl-3 text-left text-sm font-medium whitespace-nowrap w-[10%] flex space-x-2"
                      >
                        <button
                          class="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                          @click="editTopic(topic)"
                        >
                          <PencilIcon class="h-4 w-4" />
                        </button>
                        <button
                          class="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                          @click="navigateToTopic(topic)"
                        >
                          <ArrowTopRightOnSquareIcon class="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <!-- Questions Panel -->
                    <div v-if="expandedTopics[topic.topicId] && !isTopicReorderMode">
                      <div class="px-4 py-2">
                        <div class="pl-7">
                          <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                            <div class="mb-3 flex justify-between items-center">
                              <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100">Questions</h3>
                              <button
                                @click="startReorderMode(topic.topicId)"
                                class="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                              >
                                {{
                                  isReorderMode[topic.topicId]
                                    ? "Save Order"
                                    : "Reorder Questions"
                                }}
                              </button>
                            </div>
                            <div class="space-y-2">
                              <TransitionGroup name="list">
                                <div
                                  v-for="(question, index) in topic.questionList"
                                  :key="question.questionId"
                                  class="flex items-center justify-between bg-white dark:bg-gray-700 p-3 rounded-md shadow-sm"
                                  :class="{
                                    'cursor-move': isReorderMode[topic.topicId],
                                  }"
                                >
                                  <div class="flex items-center space-x-3 flex-1">
                                    <div
                                      v-if="isReorderMode[topic.topicId]"
                                      class="flex space-x-2"
                                    >
                                      <button
                                        class="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                                        @click="moveQuestion(topic.topicId, index, 'up')"
                                        :disabled="index === 0"
                                      >
                                        <ChevronUpIcon class="h-5 w-5" />
                                      </button>
                                      <button
                                        class="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                                        @click="
                                          moveQuestion(topic.topicId, index, 'down')
                                        "
                                        :disabled="
                                          index === topic.questionList.length - 1
                                        "
                                      >
                                        <ChevronDownIcon class="h-5 w-5" />
                                      </button>
                                    </div>
                                    <span class="text-sm dark:text-gray-200">
                                      {{ question.question }}
                                    </span>
                                  </div>
                                  <div class="flex items-center space-x-4">
                                    <Switch
                                      v-model="question.isVisible"
                                      @click="
                                        onQuestionVisibilityChange(
                                          question.questionId,
                                          !question.isVisible
                                        )
                                      "
                                      class="relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden"
                                      :class="[
                                        question.isVisible
                                          ? 'bg-indigo-400'
                                          : 'bg-gray-200 dark:bg-gray-600',
                                      ]"
                                    >
                                      <span
                                        aria-hidden="true"
                                        :class="[
                                          question.isVisible
                                            ? 'translate-x-4'
                                            : 'translate-x-0',
                                          'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white ring-0 shadow-sm transition duration-200 ease-in-out',
                                        ]"
                                      />
                                    </Switch>
                                    <button
                                      class="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                                      @click="editQuestion(question)"
                                    >
                                      <PencilIcon class="h-4 w-4" />
                                    </button>
                                    <button
                                      class="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                                      @click="navigateToQuestion(topic.topicId, index)"
                                    >
                                      <ArrowTopRightOnSquareIcon class="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              </TransitionGroup>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </draggable>
            </div>
          </div>
        </div>
      </div>
    </div>

    <TopicEditDialog
      v-model:visible="editDialogVisible"
      :topic="selectedTopic"
      @topic-updated="handleTopicUpdated"
    />

    <QuestionEditDialog
      v-model:visible="isQuestionEditDialogVisible"
      :question="selectedQuestion"
      @question-updated="handleQuestionUpdated"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watchEffect, watch } from "vue";
import draggable from "vuedraggable";
import { Switch } from "@headlessui/vue";
import type { Topic, Category, Question } from "@/types/topic/topic";
import {
  fetchTopicsAPI,
  fetchCategoriesAPI,
  putTopicCategoryAPI,
  putTopicVisibilityAPI,
  putTopicIsNewAPI,
  putQuestionVisibilityAPI,
  putQuestionOrderAPI,
  updateTopicOrderAPI,
  updateQuestionOrderAPI,
} from "@/api/speaking/topic";
import { HttpStatus } from "@/types/axios/httpStatus";
import {
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PencilIcon,
  Bars3Icon,
  ArrowsUpDownIcon,
  CheckIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/vue/24/outline";
import { useRouter } from "vue-router";
import TopicEditDialog from "./TopicEditDialog.vue";
import QuestionEditDialog from "./QuestionEditDialog.vue";

import toast from "@/utils/toaster";
const categories = ref<Category[]>([
  { id: 1, name: "Speaking" },
  { id: 2, name: "Writing" },
]);

const props = defineProps<{
  partId: number;
  categoryId: number;
}>();

const topics = ref<Topic[]>([]);
const expandedTopics = ref<Record<string, boolean>>({});
const isReorderMode = ref<Record<string, boolean>>({});
const isTopicReorderMode = ref(false);
const editDialogVisible = ref(false);
const selectedTopic = ref<Topic | null>(null);
const isQuestionEditDialogVisible = ref(false);
const selectedQuestion = ref<Question | null>(null);

const router = useRouter();

const onCategoryChange = async (element: Topic) => {
  const res = await putTopicCategoryAPI(String(element.topicId), element.categoryId);
  if (res.status === HttpStatus.HTTP_200_OK) {
    return toast.success("Category updated successfully");
  }
  if (res.status != HttpStatus.HTTP_200_OK) {
    return toast.error(res.message);
  }
};

const fetchCategories = async () => {
  await fetchCategoriesAPI().then((response) => {
    if (response.status === HttpStatus.HTTP_200_OK) {
      return (categories.value = response.data);
    }
    return toast.error(response.message);
  });
};

const fetchTopics = async () => {
  try {
    const response = await fetchTopicsAPI(props.partId, props.categoryId);
    topics.value = response.data;
  } catch (error) {
    console.error("Error fetching topics:", error);
  }
};

const onVisibilityChange = async (topicId: string, isVisible: boolean) => {
  await putTopicVisibilityAPI(topicId, isVisible).then((response) => {
    if (response.status === HttpStatus.HTTP_200_OK) {
      return toast.success("Visibility updated successfully");
    }
    return toast.error(response.message);
  });
};

const onToggleIsNew = async (topicId: string, isNew: boolean) => {
  await putTopicIsNewAPI(topicId, isNew).then((response) => {
    if (response.status === HttpStatus.HTTP_200_OK) {
      return toast.success("new status updated successfully");
    }
    return toast.error(response.message);
  });
};

const toggleQuestions = (topicId: string) => {
  expandedTopics.value[topicId] = !expandedTopics.value[topicId];
};

const startReorderMode = (topicId: string) => {
  if (isReorderMode.value[topicId]) {
    // Save the new order
    const topic = topics.value.find((t) => String(t.topicId) === topicId);
    if (topic) {
      onQuestionReorder(topicId, topic.questionList);
    }
  }
  isReorderMode.value[topicId] = !isReorderMode.value[topicId];
};

const moveQuestion = (topicId: string, index: number, direction: "up" | "down") => {
  const topic = topics.value.find((t) => String(t.topicId) === topicId);
  if (!topic) return;

  const newIndex = direction === "up" ? index - 1 : index + 1;
  if (newIndex < 0 || newIndex >= topic.questionList.length) return;

  const questions = [...topic.questionList];
  const temp = questions[index];
  questions[index] = questions[newIndex];
  questions[newIndex] = temp;
  topic.questionList = questions;
};

const onQuestionReorder = async (topicId: string, questions: Question[]) => {
  try {
    const questionIds = questions.map((q) => q.questionId);
    await updateQuestionOrderAPI(topicId, questionIds);
    toast.success("问题顺序更新成功");
  } catch (error: any) {
    toast.error(error.message || "更新失败");
    // Refresh to restore original order if failed
    await fetchTopics();
  }
};

const onQuestionVisibilityChange = async (questionId: string, isVisible: boolean) => {
  // TODO: Implement API call to update question visibility
  console.log("Question visibility changed:", { questionId, isVisible });
};

const editQuestion = (question: Question) => {
  selectedQuestion.value = question;
  isQuestionEditDialogVisible.value = true;
};

const handleQuestionUpdated = () => {
  fetchTopics();
};

const toggleTopicReorderMode = () => {
  if (isTopicReorderMode.value) {
    // Save the new topic order
    onTopicReorder(topics.value);
  }
  isTopicReorderMode.value = !isTopicReorderMode.value;
  // Close all expanded topics when entering reorder mode
  if (isTopicReorderMode.value) {
    expandedTopics.value = {};
  }
};

const onTopicDragEnd = () => {
  // if (isTopicReorderMode.value) {
  //   onTopicReorder(topics.value);
  // }
};

const moveTopic = (index: number, direction: "up" | "down") => {
  const newIndex = direction === "up" ? index - 1 : index + 1;
  if (newIndex < 0 || newIndex >= topics.value.length) return;

  const topicsList = [...topics.value];
  const temp = topicsList[index];
  topicsList[index] = topicsList[newIndex];
  topicsList[newIndex] = temp;
  topics.value = topicsList;
};

const onTopicReorder = async (updatedTopics: Topic[]) => {
  try {
    const topicIds = updatedTopics.map((topic) => topic.topicId);
    await updateTopicOrderAPI(topicIds);
    toast.success("话题顺序更新成功");
  } catch (error: any) {
    toast.error(error.message || "更新顺序失败");
    // Refresh the list to restore the original order
    await fetchTopics();
  }
};

const editTopic = (topic: Topic) => {
  selectedTopic.value = topic;
  editDialogVisible.value = true;
};

const handleTopicUpdated = () => {
  fetchTopics();
};

const navigateToTopic = (topic: Topic) => {
  // Open in new window
  window.open(`/topic-manager/question-manager/${topic.topicId}?q=0`, "_blank");
};

const navigateToQuestion = (topicId: string, index: number) => {
  // Open in new window
  window.open(`/topic-manager/question-manager/${topicId}?q=${index}`, "_blank");
};

onMounted(fetchCategories);
watchEffect(() => {
  fetchTopics();
});
</script>

<style scoped>
.buttons {
  margin-top: 35px;
}

.overflow-y-auto {
  overflow-y: auto;
  position: relative;
}

.list-move,
.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.list-leave-active {
  position: absolute;
}

/* Add transition for background color change */
tr {
  transition: background-color 0.2s ease;
}

.sortable-ghost {
  opacity: 0.5;
  background: #f3f4f6 !important;
}

.sortable-drag {
  background: white;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}

.drag-handle {
  cursor: move;
  touch-action: none;
}
</style>
